import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type SearchType = 'all' | 'employees' | 'contracts' | 'alerts' | 'documents' | 'leaves';

interface SearchResult {
  id: string;
  type: string;
  score: number;
  title: string;
  subtitle: string;
  data: Record<string, any>;
  url: string;
  highlights?: Record<string, string>;
  badge?: string;
}

interface Suggestion {
  id: string;
  label: string;
  sublabel: string;
  type: string;
  url: string;
}

interface GlobalSearchProps {
  placeholder?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({
  placeholder = 'Recherche intelligente...',
  inputRef,
}) => {
  const navigate = useNavigate();
  const localInputRef = useRef<HTMLInputElement | null>(null);
  const mergedRef = inputRef || localInputRef;

  const [query, setQuery] = useState('');
  // Les onglets de filtre ont été retirés : on force désormais le type "all"
  const searchType: SearchType = 'all';
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const API_BASE = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || 'http://192.168.1.4:8000/api';

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  // Effectuer la recherche
  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults([]);
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    const fetchResults = async () => {
      try {
        const token = localStorage.getItem('access');
        const headers: HeadersInit = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const params = new URLSearchParams({
          q: debouncedQuery,
          type: searchType, // restera "all"
          limit: '10',
        });

        const response = await fetch(`${API_BASE}/search/?${params}`, { headers });
        if (response.ok) {
          const data = await response.json();
          setResults(data.results || []);
          setHighlightIdx(0);
        } else if (response.status === 401) {
          console.warn('Non authentifié');
        }
      } catch (error) {
        console.error('Erreur lors de la recherche:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchResults();
  }, [debouncedQuery, searchType]);

  // Auto-complétion
  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = async () => {
      try {
        const token = localStorage.getItem('access');
        const headers: HeadersInit = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const params = new URLSearchParams({
          q: debouncedQuery,
          type: searchType,
          limit: '5',
        });

        const response = await fetch(`${API_BASE}/search/autocomplete/?${params}`, { headers });
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.suggestions || []);
        }
      } catch (error) {
        console.error('Erreur lors de l\'auto-complétion:', error);
        setSuggestions([]);
      }
    };

    fetchSuggestions();
  }, [debouncedQuery, searchType]);

  // Gestion du clavier
  const sanitizeInternalUrl = (url: string): string => {
    try {
      let u = url.trim();
      // enlever slash final
      if (u.length > 1 && u.endsWith('/')) u = u.slice(0, -1);
      return u;
    } catch {
      return url;
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allItems = results.length > 0 ? results : suggestions;

    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown' && allItems.length > 0) {
      e.preventDefault();
      setHighlightIdx(i => (i + 1) % allItems.length);
      setIsOpen(true);
    } else if (e.key === 'ArrowUp' && allItems.length > 0) {
      e.preventDefault();
      setHighlightIdx(i => (i - 1 + allItems.length) % allItems.length);
      setIsOpen(true);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[highlightIdx]) {
        navigate(sanitizeInternalUrl(results[highlightIdx].url));
      } else if (suggestions[highlightIdx]) {
        navigate(sanitizeInternalUrl(suggestions[highlightIdx].url));
      }
      setQuery('');
      setIsOpen(false);
    }
  };

  const onResultClick = (url: string) => {
    navigate(sanitizeInternalUrl(url));
    setQuery('');
    setIsOpen(false);
  };

  const getTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      employee: 'Employé',
      contract: 'Contrat',
      alert: 'Alerte',
      document: 'Document',
      leave: 'Congé',
      all: 'Tous',
    };
    return labels[type] || type;
  };

  const getTypeBadgeColor = (type: string): string => {
    const colors: Record<string, string> = {
      employee: 'badge-primary',
      contract: 'badge-info',
      alert: 'badge-warning',
      document: 'badge-accent',
      leave: 'badge-success',
    };
    return colors[type] || 'badge-neutral';
  };

  const showResults = isOpen && debouncedQuery && results.length > 0;
  const showEmpty = isOpen && debouncedQuery && results.length === 0 && !isLoading;

  return (
    <div className="w-full max-w-xl relative">
      {/* Filtres retirés volontairement ("Tous", "Employés", etc.) selon la demande. */}

      {/* Champ de recherche */}
      <div className="relative">
        <input
          ref={mergedRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 100)}
          className="input input-bordered input-sm w-full pr-10"
          placeholder={placeholder}
          autoComplete="off"
        />

        {/* Spinner de chargement */}
        {isLoading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <span className="loading loading-spinner loading-xs"></span>
          </div>
        )}
      </div>

      {/* Résultats de recherche */}
      {showResults && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg overflow-hidden max-h-96 overflow-y-auto">
          {results.map((item, idx) => (
            <button
              key={`${item.type}-${item.id}`}
              type="button"
              className={`w-full text-left px-3 py-2.5 hover:bg-base-200 border-b last:border-b-0 transition-colors ${
                idx === highlightIdx ? 'bg-base-200' : ''
              }`}
              onMouseEnter={() => setHighlightIdx(idx)}
              onMouseDown={() => onResultClick(item.url)}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{item.title}</div>
                  <div className="text-xs text-base-content/60 truncate">{item.subtitle}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {item.score ? (
                    // `calculate_relevance` (search/views.py) cumule des points
                    // sans borne : 100 pour le titre, +50 s'il commence par la
                    // requête, +50 pour le sous-titre, +10 pour le texte. Un
                    // score de 190 s'affichait donc « 1900 % ». On plafonne.
                    <span className="text-xs text-base-content/50">
                      {Math.min(100, item.score).toFixed(0)}%
                    </span>
                  ) : null}
                  <span className={`badge badge-xs ${getTypeBadgeColor(item.type)}`}>
                    {getTypeLabel(item.type)}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Suggestions d'auto-complétion */}
      {isOpen && debouncedQuery && suggestions.length > 0 && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg overflow-hidden">
          <div className="px-3 py-1 bg-base-200/50 text-xs text-base-content/60">
            Suggestions
          </div>
          {suggestions.map((item, idx) => (
            <button
              key={`suggest-${item.type}-${item.id}`}
              type="button"
              className={`w-full text-left px-3 py-2 hover:bg-base-200 border-t first:border-t-0 transition-colors ${
                idx === highlightIdx ? 'bg-base-200' : ''
              }`}
              onMouseEnter={() => setHighlightIdx(idx)}
              onMouseDown={() => onResultClick(item.url)}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{item.label}</div>
                  <div className="text-xs text-base-content/60 truncate">{item.sublabel}</div>
                </div>
                <span className={`badge badge-xs ${getTypeBadgeColor(item.type)}`}>
                  {getTypeLabel(item.type)}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* État vide */}
      {showEmpty && (
        <div className="absolute top-full left-0 right-0 z-40 mt-1 bg-base-100 border border-base-300 rounded-lg shadow p-3">
          <p className="text-xs text-base-content/70 text-center">
            Aucun résultat trouvé pour "{debouncedQuery}"
          </p>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
